import { FC, useLayoutEffect, useRef, useState } from "react"

import { Box, Chip, Tooltip, Typography, TypographyProps } from "@mui/material"
import { useTranslation } from "react-i18next"
import { IGroupScope, IGroupShort } from "ts/interfaces/Group"
import { groupScopeChipSx } from "utils/groupScope"

export type IGroupColumnItem = IGroupShort

export const joinGroupNames = (groups: IGroupColumnItem[]) =>
  groups.map((group) => group.name).join(", ")

const CHIP_GAP = 4

const scopeChipSx = (scope: IGroupScope) => {
  const chipSx = groupScopeChipSx[scope]
  return { ...chipSx, borderColor: chipSx.color }
}

const moreChipSx = {
  flexShrink: 0,
  fontWeight: 600,
  bgcolor: "mockup.neutral90",
  color: "mockup.neutral10",
  border: "1px solid",
  borderColor: "mockup.neutral40",
}

const chipLabelEllipsis = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "block",
}

const countThatFit = (widths: number[], available: number, moreWidth: number) => {
  let used = 0
  let count = 0

  for (let i = 0; i < widths.length; i++) {
    const gapBefore = count > 0 ? CHIP_GAP : 0
    const hiddenAfter = widths.length - (i + 1)
    const reserve = hiddenAfter > 0 ? CHIP_GAP + moreWidth : 0
    if (used + gapBefore + widths[i] + reserve > available) break
    used += gapBefore + widths[i]
    count += 1
  }

  return count
}

interface IGroupNameChipProps {
  group: IGroupColumnItem
  maxWidth?: number
}

const GroupNameChip: FC<IGroupNameChipProps> = ({ group, maxWidth }) => (
  <Chip
    label={group.name}
    size="small"
    sx={{
      maxWidth: maxWidth ?? "none",
      fontWeight: 500,
      border: "1px solid",
      flexShrink: maxWidth ? 1 : 0,
      minWidth: 0,
      ...scopeChipSx(group.scope),
      "& .MuiChip-label": chipLabelEllipsis,
    }}
  />
)

interface IChipLayout {
  visibleCount: number
  lastMaxWidth?: number
}

interface IProps {
  groups: IGroupColumnItem[]
  labelPrefix?: string
  variant?: TypographyProps["variant"]
}

const GroupColumnCell: FC<IProps> = ({
  groups,
  labelPrefix,
  variant = "body2",
}) => {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [chipLayout, setChipLayout] = useState<IChipLayout>({
    visibleCount: groups.length,
  })

  useLayoutEffect(() => {
    const container = containerRef.current
    const measureRow = measureRef.current
    if (!container || !measureRow) return

    const measure = () => {
      const available = container.clientWidth
      if (available <= 0) return

      const chipEls = Array.from(
        measureRow.querySelectorAll<HTMLElement>("[data-group-chip]"),
      )
      const moreEl = measureRow.querySelector<HTMLElement>("[data-group-more]")
      const moreLabel = moreEl?.querySelector(".MuiChip-label")
      if (!chipEls.length || !moreEl || !moreLabel) return

      const widths = chipEls.map((el) => el.offsetWidth)
      const widthForHidden = (hidden: number) => {
        moreLabel.textContent = `+${hidden}`
        return moreEl.offsetWidth
      }

      let count = countThatFit(widths, available, 0)
      let moreWidth = 0
      if (count < widths.length) {
        let hidden = widths.length - count
        moreWidth = widthForHidden(hidden)
        count = countThatFit(widths, available, moreWidth)
        const hiddenAfter = widths.length - count
        if (hiddenAfter !== hidden) {
          hidden = hiddenAfter
          moreWidth = widthForHidden(hidden)
          count = countThatFit(widths, available, moreWidth)
        }
        if (count === 0) count = 1
      }

      let lastMaxWidth: number | undefined
      if (count > 0) {
        let usedBeforeLast = 0
        for (let i = 0; i < count - 1; i++) {
          usedBeforeLast += widths[i] + CHIP_GAP
        }
        const reserve = count < widths.length ? CHIP_GAP + moreWidth : 0
        const spaceForLast = available - usedBeforeLast - reserve
        if (widths[count - 1] > spaceForLast) {
          lastMaxWidth = Math.max(spaceForLast, 48)
        }
      }

      setChipLayout((prev) => {
        if (
          prev.visibleCount === count &&
          prev.lastMaxWidth === lastMaxWidth
        ) {
          return prev
        }
        return { visibleCount: count, lastMaxWidth }
      })
    }

    measure()
    let cancelled = false
    document.fonts?.ready.then(() => {
      if (!cancelled) measure()
    })
    const observer = new ResizeObserver(measure)
    observer.observe(container)

    return () => {
      cancelled = true
      observer.disconnect()
    }
  }, [groups])

  if (!groups.length) {
    const emptyLabel = labelPrefix
      ? `${labelPrefix} ${t("group.noGroups")}`
      : t("group.noGroups")

    return (
      <Typography
        variant={variant}
        fontWeight={500}
        color="mockup.neutral10"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        overflow="hidden"
      >
        {emptyLabel}
      </Typography>
    )
  }

  const visibleCount = Math.min(chipLayout.visibleCount, groups.length)
  const visibleGroups = groups.slice(0, visibleCount)
  const hiddenGroups = groups.slice(visibleCount)

  return (
    <Box minWidth={0} width="100%">
      {labelPrefix && (
        <Typography
          variant={variant}
          fontWeight={500}
          color="mockup.neutral10"
          mb="4px"
          textAlign="center"
        >
          {labelPrefix}
        </Typography>
      )}
      <Box
        ref={containerRef}
        position="relative"
        display="flex"
        flexDirection="row"
        flexWrap="nowrap"
        alignItems="center"
        gap={`${CHIP_GAP}px`}
        minWidth={0}
        width="100%"
        overflow="hidden"
        justifyContent={labelPrefix ? "center" : "flex-start"}
      >
        <Box
          ref={measureRef}
          aria-hidden
          sx={{
            position: "absolute",
            visibility: "hidden",
            pointerEvents: "none",
            display: "flex",
            gap: `${CHIP_GAP}px`,
            width: "max-content",
          }}
        >
          {groups.map((group) => (
            <Box key={group.id} component="span" data-group-chip display="inline-flex">
              <GroupNameChip group={group} />
            </Box>
          ))}
          <Box component="span" data-group-more display="inline-flex">
            <Chip label="+0" size="small" sx={moreChipSx} />
          </Box>
        </Box>
        {visibleGroups.map((group, index) => {
          const maxWidth =
            index === visibleGroups.length - 1
              ? chipLayout.lastMaxWidth
              : undefined

          if (!maxWidth) {
            return (
              <Box key={group.id} component="span" display="inline-flex">
                <GroupNameChip group={group} />
              </Box>
            )
          }

          return (
            <Tooltip key={group.id} title={group.name}>
              <Box
                component="span"
                display="inline-flex"
                minWidth={0}
                maxWidth={maxWidth}
                overflow="hidden"
              >
                <GroupNameChip group={group} maxWidth={maxWidth} />
              </Box>
            </Tooltip>
          )
        })}
        {hiddenGroups.length > 0 && (
          <Tooltip
            title={
              <Box display="flex" flexWrap="wrap" gap={`${CHIP_GAP}px`}>
                {hiddenGroups.map((group) => (
                  <GroupNameChip key={group.id} group={group} />
                ))}
              </Box>
            }
          >
            <Chip
              label={t("group.moreGroups", { count: hiddenGroups.length })}
              size="small"
              aria-label={joinGroupNames(hiddenGroups)}
              sx={moreChipSx}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  )
}

export default GroupColumnCell
